import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import Layout from './components/Layout';
import LoadingOverlay from './components/LoadingOverlay';
import { hasFullAccess } from './utils/roles';

// Lazy loading de páginas para code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'));
const CallbackPage = lazy(() => import('./pages/CallbackPage'));
const LogoutPage = lazy(() => import('./pages/LogoutPage'));
const EmpresasPage = lazy(() => import('./pages/EmpresasPage'));
const BicosPage = lazy(() => import('./pages/BicosPage'));
const TiposCombustivelPage = lazy(() => import('./pages/TiposCombustivelPage'));
const CoordenadoresPage = lazy(() => import('./pages/CoordenadoresPage'));
const GerentesPage = lazy(() => import('./pages/GerentesPage'));
const AfericoesPage = lazy(() => import('./pages/AfericoesPage'));
const RelatorioPage = lazy(() => import('./pages/RelatorioPage'));
const RelatoriosSemanaisPage = lazy(() => import('./pages/RelatoriosSemanaisPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const Error401Page = lazy(() => import('./pages/Error401Page'));
const Error403Page = lazy(() => import('./pages/Error403Page'));
const Error404Page = lazy(() => import('./pages/Error404Page'));
const Error500Page = lazy(() => import('./pages/Error500Page'));
const HomeRedirect = lazy(() => import('./components/HomeRedirect'));

function PrivateRoute({ children, requiredRole }) {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingOverlay />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Se requiredRole for especificado, verifica se o usuário tem acesso total OU a role específica
  if (requiredRole) {
    const hasAccess = hasFullAccess(user?.role) || user?.role === requiredRole;
    if (!hasAccess) {
      return <Navigate to="/403" replace />;
    }
  }

  return children;
}

// Componente para detectar logout=success e limpar cookies
function LogoutSuccessHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('logout') === 'success') {
      console.log('[App] Detectado logout=success, removendo cookies e limpando storage...');
      
      // Remover cookie de autenticação do servidor
      // Tentar diferentes variações do nome do cookie e configurações
      const cookieNames = [
        '.AspNetCore.Cookies',
        '.AspNetCore.Identity.Application',
        'idsrv',
        'idsrv.session',
        'OpenIdConnect.nonce'
      ];
      
      const domain = window.location.hostname;
      const domainParts = domain.split('.');
      const domainsToTry = [domain];
      
      // Adicionar domínio pai se houver (ex: .localhost, .example.com)
      if (domainParts.length > 1) {
        domainsToTry.push('.' + domainParts.slice(-2).join('.'));
        domainsToTry.push('.' + domainParts.slice(-1).join('.'));
      }
      
      // Tentar remover cookies com diferentes configurações
      domainsToTry.forEach(dom => {
        cookieNames.forEach(cookieName => {
          const paths = ['/', '/connect', '/account', '/signin'];
          paths.forEach(path => {
            // Remover com diferentes configurações de SameSite e Secure
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${dom};`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${dom}; SameSite=Lax;`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${dom}; SameSite=Strict;`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${dom}; SameSite=None; Secure;`;
          });
        });
      });
      
      // Limpar tokens do localStorage/sessionStorage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      sessionStorage.clear();
      
      // Limpar dados do OIDC
      const oidcStorageKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('oidc.user:') || key.startsWith('oidc.')
      );
      oidcStorageKeys.forEach(key => localStorage.removeItem(key));
      
      console.log('[App] Cookies e storage limpos. Removendo parâmetro logout=success da URL...');
      
      // Remover o parâmetro logout=success da URL e redirecionar
      const newSearch = new URLSearchParams(location.search);
      newSearch.delete('logout');
      const newUrl = location.pathname + (newSearch.toString() ? `?${newSearch.toString()}` : '');
      navigate(newUrl, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  return null;
}

function App() {
  return (
    <Suspense fallback={<LoadingOverlay />}>
      <LogoutSuccessHandler />
      <Routes>
        <Route
          path="/login"
          element={
            <Suspense fallback={<LoadingOverlay />}>
              <LoginPage />
            </Suspense>
          }
        />
        <Route
          path="/callback"
          element={
            <Suspense fallback={<LoadingOverlay />}>
              <CallbackPage />
            </Suspense>
          }
        />
        <Route
          path="/logout"
          element={
            <Suspense fallback={<LoadingOverlay />}>
              <LogoutPage />
            </Suspense>
          }
        />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={
            <Suspense fallback={<LoadingOverlay />}>
              <HomeRedirect />
            </Suspense>
          } />
          <Route
            path="dashboard"
            element={
              <PrivateRoute requiredRole="Admin">
                <Suspense fallback={<LoadingOverlay />}>
                  <DashboardPage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="admin/empresas"
            element={
              <PrivateRoute requiredRole="Admin">
                <Suspense fallback={<LoadingOverlay />}>
                  <EmpresasPage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="admin/bicos"
            element={
              <PrivateRoute requiredRole="Admin">
                <Suspense fallback={<LoadingOverlay />}>
                  <BicosPage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="admin/tipos-de-combustiveis"
            element={
              <PrivateRoute requiredRole="Admin">
                <Suspense fallback={<LoadingOverlay />}>
                  <TiposCombustivelPage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="admin/coordenadores"
            element={
              <PrivateRoute requiredRole="Admin">
                <Suspense fallback={<LoadingOverlay />}>
                  <CoordenadoresPage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="admin/gerentes"
            element={
              <PrivateRoute requiredRole="Admin">
                <Suspense fallback={<LoadingOverlay />}>
                  <GerentesPage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="admin/afericoes"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingOverlay />}>
                  <AfericoesPage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="relatorio"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingOverlay />}>
                  <RelatorioPage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="admin/relatorios-semanais"
            element={
              <PrivateRoute requiredRole="Admin">
                <Suspense fallback={<LoadingOverlay />}>
                  <RelatoriosSemanaisPage />
                </Suspense>
              </PrivateRoute>
            }
          />
        </Route>
        <Route
          path="/401"
          element={
            <Suspense fallback={<LoadingOverlay />}>
              <Error401Page />
            </Suspense>
          }
        />
        <Route
          path="/403"
          element={
            <Suspense fallback={<LoadingOverlay />}>
              <Error403Page />
            </Suspense>
          }
        />
        <Route
          path="/500"
          element={
            <Suspense fallback={<LoadingOverlay />}>
              <Error500Page />
            </Suspense>
          }
        />
        <Route
          path="*"
          element={
            <Suspense fallback={<LoadingOverlay />}>
              <Error404Page />
            </Suspense>
          }
        />
      </Routes>
    </Suspense>
  );
}

export default App;
